import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
    cases: [],
    currentCase: null,
    caseHistory: [], // renamed from attachments
    patientForms: {}, // new key to store the forms data
    isLoading: false,
    error: null,
};

// ==================== Async Thunks ====================

// Fetch all cases
export const fetchCases = createAsyncThunk(
    'cases/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/cases');
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch cases'
            );
        }
    }
);

// ==================== NEW: Fetch all cases for a specific patient ====================
export const fetchCasesByPatient = createAsyncThunk(
    'cases/fetchByPatient',
    async (patientId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/cases/patients/${patientId}/cases`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch cases for this patient'
            );
        }
    }
);


// Fetch single case by ID
export const fetchCase = createAsyncThunk(
    'cases/fetchOne',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/cases/${id}`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch case details'
            );
        }
    }
);

// ==================== Store approve/reject case ====================
export const approveOrRejectCase = createAsyncThunk(
    'cases/approveOrReject',
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post('/cases/approve-history', data, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data.data; // new case_history record
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to approve/reject case'
            );
        }
    }
);

// ==================== NEW: Fetch case history by case ID ====================
export const fetchCaseHistory = createAsyncThunk(
    'cases/fetchHistory',
    async (caseId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/cases/${caseId}/history`);
            return response.data.history || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch case history'
            );
        }
    }
);

// ==================== NEW: Check patient forms ====================
export const checkPatientForms = createAsyncThunk(
    'cases/checkPatientForms',
    async ({ patientId, caseId }, { rejectWithValue }) => {
        try {
            const response = await api.get('/cases/patient-case/check', {
                params: { patient_id: patientId, case_id: caseId },
            });
            return response.data; // { forms: {...}, case: {...} }
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to check patient forms'
            );
        }
    }
);

// ==================== Slice ====================
const casesSlice = createSlice({
    name: 'cases',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentCase: (state, action) => {
            state.currentCase = action.payload;
        },
        clearCurrentCase: (state) => {
            state.currentCase = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL CASES
            .addCase(fetchCases.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCases.fulfilled, (state, action) => {
                state.isLoading = false;
                state.cases = action.payload;
            })
            .addCase(fetchCases.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // ==================== FETCH CASES BY PATIENT ====================
            .addCase(fetchCasesByPatient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCasesByPatient.fulfilled, (state, action) => {
                state.isLoading = false;
                state.cases = action.payload;
            })
            .addCase(fetchCasesByPatient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // FETCH SINGLE CASE
            .addCase(fetchCase.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCase.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentCase = action.payload;
            })
            .addCase(fetchCase.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // APPROVE / REJECT CASE
            .addCase(approveOrRejectCase.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(approveOrRejectCase.fulfilled, (state, action) => {
                state.isLoading = false;
                // Optionally push the new case_history into the current case's history
                state.caseHistory.push(action.payload);
            })
            .addCase(approveOrRejectCase.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // ==================== FETCH CASE HISTORY ====================
            .addCase(fetchCaseHistory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCaseHistory.fulfilled, (state, action) => {
                state.isLoading = false;
                state.caseHistory = action.payload;
            })
            .addCase(fetchCaseHistory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // ==================== CHECK PATIENT FORMS ====================
            .addCase(checkPatientForms.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(checkPatientForms.fulfilled, (state, action) => {
                state.isLoading = false;
                // Store forms data and case info
                state.patientForms = action.payload.forms; // new key for forms
                state.currentCase = action.payload.case;    // optionally overwrite currentCase
            })
            .addCase(checkPatientForms.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, setCurrentCase, clearCurrentCase } =
    casesSlice.actions;

export default casesSlice.reducer;
